/**
 * Is this tab running a build we have since replaced?
 *
 * **The problem is not caching.** The cache headers are already right: `index.html` is served
 * `no-cache, must-revalidate` and the assets are content-hashed and `immutable`, so any reload gets
 * the new build. The problem is that a single-page app fetches `index.html` exactly once. Client-side
 * routing never fetches it again, so a tab left open across a deploy keeps running the old JavaScript
 * indefinitely — for a reviewer who lives in one tab all day, that can be days. This bit us
 * concretely: the presence beacon shipped on 2026-08-06 and could not have reached any tab opened
 * before it, and nothing in the app would ever have said so.
 *
 * Nothing new has to be generated to detect it. The release SHA is already baked into the bundle at
 * build time as `VITE_SENTRY_RELEASE` (`vite.config.ts` assigns it from `SENTRY_RELEASE`) and the API
 * already has the same value in its environment, so the check is a string comparison against a field
 * on `/health`.
 *
 * ## Why this cannot reload-loop
 *
 * A reload loop on the reviewer's machine is by far the worst outcome available here — it would make
 * the tool unusable and destroy work every few seconds. Three independent bounds prevent it, and
 * they matter because a deploy is genuinely not atomic: the API is a systemd unit and the web is a
 * static directory served by `python3 -m http.server`, rsynced separately, so there is a real window
 * in which the API already reports the new SHA while `dist/` still holds the old one.
 *
 * 1. **At most one automatic reload per target release**, recorded in `sessionStorage`. If we reload
 *    towards release X and come back still not running X — exactly what the rsync window produces —
 *    we never auto-reload for X again. The banner stays, and the reviewer decides.
 * 2. **A missing or empty release on either side disables the check entirely.** A dev build, a
 *    deployment that never set `SENTRY_RELEASE`, or an API that does not report one must not be
 *    interpreted as "everything is stale".
 * 3. **Auto-reload only ever happens at a navigation boundary with nothing typed.** It cannot fire
 *    while somebody is mid-sentence in a feedback box.
 */

import { hasRegisteredUnsavedWork } from './unsavedWork.js';

/** The release this bundle was built from. Empty in dev, and empty disables the whole mechanism. */
export const BUILD_RELEASE = String(import.meta.env.VITE_SENTRY_RELEASE ?? '').trim();

const AUTO_RELOAD_MARKER_PREFIX = 'author.version_reload.';

export interface VersionState {
  /** The release the API says is deployed, or null if unknown/unreported. */
  readonly deployedRelease: string | null;
  /** True when both sides reported a release and they differ. */
  readonly updateAvailable: boolean;
}

/**
 * Reads the release of the STATIC BUILD currently being served, from `/version.json`.
 *
 * **Not from the API, and the deploy script is the reason.** `scripts/deploy-prod.sh` runs
 * `npm run build` — which rewrites the very `dist/` directory Caddy serves — and restarts the API
 * several lines later. So the ordinary mid-deploy state is *new static, old API*: a tab that has
 * just loaded the newest possible bundle would be told it was out of date, and would reload to get
 * the build it already had. Comparing this bundle's baked-in release against the manifest sitting
 * beside it in the same directory compares like with like, and answers the only question that
 * matters: is the file I was built from still the file being served?
 *
 * Cache-busted twice over — a `no-store` header from Caddy and a unique query string — because a
 * cached answer here does not degrade the feature, it inverts it.
 *
 * Every failure returns null, which reads as "no information" and never as "you are stale".
 */
export async function fetchServedRelease(signal?: AbortSignal): Promise<string | null> {
  try {
    if (typeof fetch !== 'function') {
      return null;
    }
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      method: 'GET',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      ...(signal ? { signal } : {}),
    });
    // A 404 or an SPA fallback serving index.html both land here as "not JSON" and are ignored.
    // Silence is the correct response to a deployment that has no manifest yet.
    if (!response.ok) {
      return null;
    }
    const payload: unknown = await response.json().catch(() => null);
    if (!payload || typeof payload !== 'object') {
      return null;
    }
    const release = (payload as { release?: unknown }).release;
    if (typeof release !== 'string' || release.trim() === '') {
      return null;
    }
    return release.trim();
  } catch {
    return null;
  }
}

/**
 * The comparison, isolated so it can be tested without a network or a DOM.
 *
 * **Absence is never staleness.** If either side is missing a release the answer is "no update",
 * because the alternative — treating an unknown as a mismatch — would show a permanent "reload"
 * banner in local development and on any deployment that forgot to set `SENTRY_RELEASE`, and a
 * banner that is always on is a banner nobody reads.
 */
export function compareReleases(buildRelease: string, deployedRelease: string | null): VersionState {
  const build = buildRelease.trim();
  const deployed = (deployedRelease ?? '').trim();
  if (build === '' || deployed === '') {
    return { deployedRelease: deployed === '' ? null : deployed, updateAvailable: false };
  }
  return { deployedRelease: deployed, updateAvailable: build !== deployed };
}

/**
 * Is there work in progress that a reload would destroy?
 *
 * Two independent signals, OR'd, because each covers the other's blind spot:
 *
 * 1. **The registry** (`lib/unsavedWork.ts`) — what each screen says about itself, including the
 *    things the page cannot show: a content edit that is not a text field, a note whose panel is
 *    closed. This is the authoritative one.
 * 2. **The DOM sweep** below — a backstop for any screen that never registered.
 *
 * Deliberately over-cautious: a stray character in a filter box blocks the automatic reload and
 * leaves the banner, which costs nothing. Losing a half-written review comment costs the comment,
 * and the reviewer would have no idea why it vanished. Scott wrote 569 words of feedback on
 * 2026-08-10.
 */
export function hasUnsavedWork(): boolean {
  return hasRegisteredUnsavedWork() || hasUnsavedTypedText();
}

/** The DOM half of {@link hasUnsavedWork}. Exported for its own tests. */
export function hasUnsavedTypedText(): boolean {
  try {
    if (typeof document === 'undefined') {
      return false;
    }
    const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'textarea, input[type="text"], input:not([type])',
    );
    for (const field of fields) {
      if (field.disabled || field.readOnly) {
        continue;
      }
      if (typeof field.value === 'string' && field.value.trim() !== '') {
        return true;
      }
    }
    // A `contenteditable` surface holds text the same way and would be missed by the query above.
    const editables = document.querySelectorAll<HTMLElement>('[contenteditable="true"]');
    for (const editable of editables) {
      if ((editable.textContent ?? '').trim() !== '') {
        return true;
      }
    }
    return false;
  } catch {
    // If we cannot tell, assume there IS unsaved text. The safe direction is never to auto-reload.
    return true;
  }
}

/**
 * Have we already spent this tab's one automatic reload on this particular release?
 *
 * `sessionStorage`, not `localStorage`: the budget belongs to this tab's session. A new tab starts
 * fresh, which is correct — a fresh tab loading the old bundle is a different event from a tab that
 * already tried and failed.
 */
export function hasAlreadyAutoReloadedFor(release: string): boolean {
  try {
    if (typeof sessionStorage === 'undefined') {
      return true;
    }
    return sessionStorage.getItem(`${AUTO_RELOAD_MARKER_PREFIX}${release}`) === 'true';
  } catch {
    // A tab with storage disabled gets no automatic reloads at all, only the banner. Unbounded
    // automatic reloads with no way to record that we tried is the one thing worse than the banner.
    return true;
  }
}

/**
 * Records that this tab has spent its automatic reload on `release`, and **reports whether the
 * record actually stuck**.
 *
 * The boolean is the whole point, and it was found missing by cross-model review. The earlier
 * version swallowed a write failure and reloaded anyway — so with a full or evicting
 * `sessionStorage` the marker never persisted, the next mount saw no marker, and the tab reloaded
 * again, and again. A caller that cannot prove it recorded the attempt must not take the attempt.
 */
export function markAutoReloadedFor(release: string): boolean {
  try {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    const key = `${AUTO_RELOAD_MARKER_PREFIX}${release}`;
    sessionStorage.setItem(key, 'true');
    // Read back rather than trusting the write. Some browsers accept a `setItem` in private mode
    // and discard it, which is indistinguishable from success without this line.
    return sessionStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

/**
 * The whole decision, as one pure predicate so the policy is testable without a browser.
 *
 * Every condition must hold. They are ordered cheapest-first, but the order carries no meaning —
 * this is a conjunction, and each clause exists to stop a distinct way of doing harm.
 */
export function shouldAutoReload(input: {
  readonly updateAvailable: boolean;
  readonly deployedRelease: string | null;
  readonly atNavigationBoundary: boolean;
  readonly hasUnsavedWork: boolean;
  readonly alreadyReloadedForRelease: boolean;
}): boolean {
  if (!input.updateAvailable || !input.deployedRelease) {
    return false;
  }
  if (!input.atNavigationBoundary) {
    return false;
  }
  if (input.hasUnsavedWork) {
    return false;
  }
  if (input.alreadyReloadedForRelease) {
    return false;
  }
  return true;
}
